import { HttpEventType } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FileUploadsStore } from "./file-uploads.store";
import { BehaviorSubject, concatMap, filter, switchMap, tap } from "rxjs";
import { addEntities, deleteEntities, selectFirst, updateEntities } from "@ngneat/elf-entities";
import { FileUpload } from "./file-upload.model";
import { retryBackoff } from "backoff-rxjs";
import { v4 as uuidv4 } from "uuid";
import { StorageService } from "../services/storage.service";
import { ApiService } from "../services/api.service";

import { FileTranslation } from "./file-translation.model";
import { FileTranslationsService } from "./file-translations.service";

@Injectable({ providedIn: "root" })
export class FileUploadsService {
	constructor(
		private fileUploadsStore: FileUploadsStore,
		private storageService: StorageService,
		private apiService: ApiService,
		private fileTranslationsService: FileTranslationsService,
	) {
		this.upload();
	}

	public currentUploadProgress$ = new BehaviorSubject(0);

	INIT_INTERVAL_MS = 100; // 100 ms
	MAX_INTERVAL_MS = 60 * 1000; // 60 sec

	private upload = () => {
		this.fileUploadsStore.store
			.pipe(
				selectFirst(),
				filter((x) => x !== undefined),
				concatMap((x) => this.storageService.getBlobWithKey(x.id)),
				switchMap((x) => {
					this.currentUploadProgress$.next(0);
					return this.apiService.uploadFiles$([new File([x.blob], x.key)]).pipe(
						tap((reponse) => {
							if (reponse.type === HttpEventType.Response) {
								const fileTranslation: FileTranslation = {
									id: x.key,
									file: x.blob,
									path: reponse.body.data[0].path,
								};

								this.fileTranslationsService.add(fileTranslation);
								this.remove(x.key);
							}
							if (reponse.type === HttpEventType.UploadProgress) {
								this.currentUploadProgress$.next(reponse.loaded / reponse.total);
							}
						}),
					);
				}),
				retryBackoff({
					initialInterval: this.INIT_INTERVAL_MS,
					maxInterval: this.MAX_INTERVAL_MS,
					resetOnSuccess: true,
				}),
			)
			.subscribe();
	};

	add = async (type: string, blob: Blob): Promise<string> => {
		const id = uuidv4();
		const fileUpload: FileUpload = {
			id: id,
			type: type,
		};
		await this.storageService.set(fileUpload.id, blob);
		this.fileUploadsStore.store.update(addEntities(fileUpload));
		return id;
	};

	update(id: string, fileUpload: Partial<FileUpload>) {
		this.fileUploadsStore.store.update(updateEntities(id, fileUpload));
	}

	remove(id: string) {
		this.fileUploadsStore.store.update(deleteEntities(id));
	}
}
